import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  Avatar,
  Typography,
  Badge,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  Chip,
  Checkbox,
  ListItemText,
  Skeleton,
  LinearProgress,
  useTheme,
  Tooltip,
  Divider,
  Snackbar,
  Alert,
  Stack,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { useNavigate } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PushPinIcon from "@mui/icons-material/PushPin";
import { getChannelIcon, getClientProfilePictureUrl, getInitials } from "../utils/utils";
import { getWhatsAppWindowState } from "../utils/whatsappWindowUtils";
import {
  getMetaMessagingWindowState,
  getMetaChannelLabel,
  isMetaMessagingWindowChannel,
} from "../utils/metaMessagingWindowUtils";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { fetchData, getPersonalWhatsappStatus } from "../api/client";
import { workspaceScopedQueryKey } from "../utils/workspaceQueryScope";
import { useConversationListLogic } from "../hooks/useConversationListLogic";
import { translateQuickFilterLabel } from "../i18n/chatListFilterLabels";
import { formatQuickFilterCountLabel } from "../utils/conversationQuickFilters";
import { useConversationFilterCounts } from "../hooks/useConversationFilterCounts";
import StatusChipSelector, { normalizeStatusInput } from "./StatusChipSelector";
import AssigneeSelector from "./AssigneeSelector";
import { useRequests } from "../hooks/useRequests";
import { useUsers } from "../hooks/useUsers";
import { useAgents } from "../hooks/useAgents";
import { useChannels } from "../hooks/useChannels";
import { useTypes } from "../hooks/useTypes";
import AdvancedSearchModal from "./AdvancedSearchModal";
import FilterChipsCollapsible from "./FilterChipsCollapsible";
import { getSavedFilters } from "../utils/savedFilters";
import {
  chatListStateToSearchFilterState,
  chatListStateToExpandedSearchFilterState,
  EMPTY_SEARCH_FILTER_STATE,
  filterStateHasAdvancedCriteria,
  filterStateToModalInitial,
  filterStateToUrlString,
  mergeQuickFiltersAfterAdvancedApply,
  modalPayloadToFilterState,
  normalizeSearchFilterState,
  TAG_NO_TAG,
} from "../utils/searchFilterState";
import { isWhatsAppQrChannelTipo } from "../constants/channelCatalog";
import { getRequestStatusChangeErrorMessage } from "../utils/status";
import {
  getAutomationBlockMessage,
  isAutomationRuleBlockError,
} from "../utils/automationBlockNotice";
import AutomationActionBlockedDialog from "./AutomationActionBlockedDialog";
import { sanitizeTelephonyListPreview } from "../utils/partnerApiAuditDisplay";
import ClientRelationshipBadge from "./Chat/ClientRelationshipBadge";

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => [
    { id: n.id, id_pai: n.id_pai ?? null, nome: n.nome || String(n.id) },
    ...flattenTypesTree(n.children || []),
  ]);
}

function formatDateChip(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, y, m, d] = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return `${d}/${m}/${y}`;
  }
  return value;
}

/** Estilo do pill por tipo de filtro (status usa cores do tema) */
function getFilterPillStyle(filter, theme) {
  const statusColors = {
    Pendente: theme.palette.status?.pending,
    "Em Atendimento": theme.palette.status?.inProgress,
    "Contato Futuro": theme.palette.status?.future,
    Concluída: theme.palette.status?.success,
    Perdida: theme.palette.status?.unsuccessful,
    Cancelada: theme.palette.status?.canceled,
  };
  const bg = statusColors[filter];
  if (!bg) return {};
  const useLightText = ["Concluída", "Perdida", "Cancelada"].includes(filter);
  return {
    backgroundColor: bg,
    color: useLightText ? theme.palette.common.white : theme.palette.text.primary,
    "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.8 },
  };
}

const PERSONAL_WHATSAPP_QUICK_FILTER = "WhatsApp pessoal";

const ConversationList = ({
  conversations,
  conversationsTimeoutMessage = null,
  workspaceEmbedded = false,
  selectedConversation,
  onSelectConversation,
  refetchConversations,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  initialLoading,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  advancedFilterState,
  applyChatFilterState,
  clearAdvancedFilterState,
}) => {
  const { t: tChat } = useTranslation("chatList");
  const { t: tPane } = useTranslation("chat");
  const navigate = useNavigate();
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [savedFiltersMenu, setSavedFiltersMenu] = useState([]);
  const {
    filterAnchor,
    contextMenuAnchor,
    selectedForContextMenu,
    ownershipFilters,
    additionalFilters,
    isMeuDiaActive,
    setMeuDia,
    listRef,
    handleSearchChange,
    handleFilterClick,
    handleFilterClose,
    toggleFilter,
    handleOpenInPopup,
    handleContextMenu,
    handleContextMenuClose,
    handleItemClick,
    filteredConversations,
    bottomRef,
  } = useConversationListLogic({
    conversations,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    selectedConversation,
    onSelectConversation,
    refetchConversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const { countsByLabel, unreadCapped } = useConversationFilterCounts({
    filters,
    advancedFilterState,
  });
  const labeledFilter = (filter) =>
    formatQuickFilterCountLabel(
      translateQuickFilterLabel(filter, tChat),
      countsByLabel[filter],
      { capped: filter === "Não Lidas" && unreadCapped }
    );

  // Gate: evita chamadas para /users/me/whatsapp-personal quando o recurso
  // não está habilitado no perfil do usuário.
  const { data: meRoot, isSuccess: isMeReady } = useQuery(
    workspaceScopedQueryKey("me"),
    () => fetchData("/users/me"),
    { staleTime: 60000 }
  );
  const wpPersonalEnabled = Boolean(meRoot?.data?.whatsapp_personal?.enabled);

  const { data: personalWaStatus, isSuccess: isPersonalWaStatusReady } = useQuery(
    ["personal-whatsapp-status"],
    async () => {
      const r = await getPersonalWhatsappStatus();
      return r?.data ?? r ?? {};
    },
    {
      enabled: wpPersonalEnabled,
      refetchInterval: wpPersonalEnabled ? 5000 : false,
    }
  );
  const showPersonalWhatsappFilter = wpPersonalEnabled && Boolean(personalWaStatus?.enabled ?? true);

  /** Só remove o chip após o GET do status — no loading, `enabled` é undefined e não pode ser tratado como "desligado". */
  useEffect(() => {
    // Se /users/me já diz que está desligado, remove imediatamente sem esperar o status.
    if (isMeReady && !wpPersonalEnabled) {
      setFilters((prev) => {
        if (!Array.isArray(prev) || !prev.includes(PERSONAL_WHATSAPP_QUICK_FILTER)) return prev;
        return prev.filter((f) => f !== PERSONAL_WHATSAPP_QUICK_FILTER);
      });
      return;
    }
    if (!isPersonalWaStatusReady) return;
    if (personalWaStatus?.enabled) return;
    setFilters((prev) => {
      if (!Array.isArray(prev) || !prev.includes(PERSONAL_WHATSAPP_QUICK_FILTER)) return prev;
      return prev.filter((f) => f !== PERSONAL_WHATSAPP_QUICK_FILTER);
    });
  }, [isMeReady, wpPersonalEnabled, isPersonalWaStatusReady, personalWaStatus?.enabled, setFilters]);

  const { userData } = useAuthContext();
  const theme = useTheme();

  const { updateMutation } = useRequests();
  // Precisa incluir ChatBots (ura=1) no seletor de responsável.
  const { data: usersResp, isLoading: usersLoading } = useUsers(1, 1000, "", "todos", {
    order: "nome",
  });
  const { data: agentsResp } = useAgents(1, 200, "");
  const { data: channelsData } = useChannels(1, 200, "");
  const { data: typesData } = useTypes(1, 500, "");

  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const channels = channelsData?.channels ?? [];

  const typesFlat = useMemo(
    () => flattenTypesTree(typesData?.types || []),
    [typesData?.types]
  );
  const typeIdToName = useMemo(
    () => Object.fromEntries(typesFlat.map((t) => [String(t.id), t.nome || `Tipo ${t.id}`])),
    [typesFlat]
  );

  const normalizedAdvanced = useMemo(
    () => normalizeSearchFilterState(advancedFilterState || EMPTY_SEARCH_FILTER_STATE),
    [advancedFilterState]
  );

  const hasActiveFilters =
    (Array.isArray(filters) && filters.length > 0) ||
    filterStateHasAdvancedCriteria(normalizedAdvanced);

  const hasSearchTerm = Boolean((searchTerm || "").trim());

  const emptyListVariant = useMemo(() => {
    if (hasSearchTerm && hasActiveFilters) return "searchAndFilters";
    if (hasSearchTerm) return "search";
    if (hasActiveFilters) return "filters";
    return "default";
  }, [hasSearchTerm, hasActiveFilters]);

  const handleClearAllFilters = useCallback(() => {
    setFilters([]);
    if (typeof clearAdvancedFilterState === "function") {
      clearAdvancedFilterState();
    }
  }, [setFilters, clearAdvancedFilterState]);

  const handleOpenExpandedSearchResults = useCallback(() => {
    const state = chatListStateToExpandedSearchFilterState(
      searchTerm,
      filters,
      normalizedAdvanced
    );
    navigate(`/search-results?${filterStateToUrlString(state)}`);
  }, [searchTerm, filters, normalizedAdvanced, navigate]);

  const refreshSavedFiltersMenu = useCallback(() => {
    setSavedFiltersMenu(getSavedFilters());
  }, []);

  useEffect(() => {
    if (filterAnchor) refreshSavedFiltersMenu();
  }, [filterAnchor, refreshSavedFiltersMenu]);

  const advancedFilterChips = useMemo(() => {
    const s = normalizedAdvanced;
    const chips = [];
    s.ids_tipos.forEach((id) => {
      chips.push({ key: `t-${id}`, label: typeIdToName[String(id)] ?? `Tipo ${id}`, kind: "ids_tipos", value: id });
    });
    s.ids_subtipo1.forEach((id) => {
      chips.push({ key: `s1-${id}`, label: typeIdToName[String(id)] ?? `Subtipo ${id}`, kind: "ids_subtipo1", value: id });
    });
    s.ids_subtipo2.forEach((id) => {
      chips.push({ key: `s2-${id}`, label: typeIdToName[String(id)] ?? `Subtipo ${id}`, kind: "ids_subtipo2", value: id });
    });
    s.ids_subtipo3.forEach((id) => {
      chips.push({ key: `s3-${id}`, label: typeIdToName[String(id)] ?? `Subtipo ${id}`, kind: "ids_subtipo3", value: id });
    });
    s.ids_origens.forEach((id) => {
      const ch = channels.find((c) => String(c.id) === String(id));
      chips.push({
        key: `o-${id}`,
        label: ch?.nome || ch?.name || `Canal ${id}`,
        kind: "ids_origens",
        value: id,
      });
    });
    s.ids_usuarios.forEach((id) => {
      const u = users.find((x) => String(x.id) === String(id));
      chips.push({
        key: `u-${id}`,
        label: `Responsável: ${u?.nome || u?.name || id}`,
        kind: "ids_usuarios",
        value: id,
      });
    });
    (s.ids_agentes || []).forEach((id) => {
      const a = agents.find((x) => String(x.id ?? x.id_agente ?? x.agentId) === String(id));
      chips.push({
        key: `ag-${id}`,
        label: `Responsável: ${a?.name || a?.nome || id}`,
        kind: "ids_agentes",
        value: id,
      });
    });
    s.ids_usuarios_interacao.forEach((id) => {
      const u = users.find((x) => String(x.id) === String(id));
      chips.push({
        key: `ui-${id}`,
        label: `Interagiu: ${u?.nome || u?.name || id}`,
        kind: "ids_usuarios_interacao",
        value: id,
      });
    });
    (s.ids_agentes_interacao || []).forEach((id) => {
      const a = agents.find((x) => String(x.id ?? x.id_agente ?? x.agentId) === String(id));
      chips.push({
        key: `agi-${id}`,
        label: `Interagiu: ${a?.name || a?.nome || id}`,
        kind: "ids_agentes_interacao",
        value: id,
      });
    });
    s.tags.forEach((tag) => {
      chips.push({
        key: `tag-${tag}`,
        label: tag === TAG_NO_TAG ? "Sem tag" : `Tag: ${tag}`,
        kind: "tags",
        value: tag,
      });
    });
    if (s.data_inicio) {
      chips.push({ key: "di", label: `De: ${formatDateChip(s.data_inicio)}`, kind: "data_inicio" });
    }
    if (s.data_fim) {
      chips.push({ key: "df", label: `Até: ${formatDateChip(s.data_fim)}`, kind: "data_fim" });
    }
    if (s.tipo_responsavel) {
      const map = { fila: "Fila", humanos: "Humanos", chatbots: "ChatBots", agentes: "Agentes de IA" };
      chips.push({ key: "tr", label: `Responsável: ${map[s.tipo_responsavel] || s.tipo_responsavel}`, kind: "tipo_responsavel" });
    }
    if (s.include_message_text) {
      chips.push({ key: "msg", label: "Texto das mensagens", kind: "include_message_text" });
    }
  return chips;
  }, [normalizedAdvanced, typeIdToName, channels, users, agents]);

  const removeAdvancedChip = (chip) => {
    if (typeof applyChatFilterState !== "function") return;
    const next = { ...normalizedAdvanced };
    if (chip.kind === "data_inicio") next.data_inicio = "";
    else if (chip.kind === "data_fim") next.data_fim = "";
    else if (chip.kind === "tipo_responsavel") next.tipo_responsavel = "";
    else if (chip.kind === "include_message_text") next.include_message_text = false;
    else if (Array.isArray(next[chip.kind])) {
      next[chip.kind] = next[chip.kind].filter((v) => String(v) !== String(chip.value));
    }
    applyChatFilterState(next);
  };

  const handleApplySavedFilter = (entry) => {
    if (typeof applyChatFilterState === "function" && entry?.state) {
      applyChatFilterState(entry.state);
    }
    handleFilterClose();
  };

  const handleOpenAdvanced = () => {
    handleFilterClose();
    setShowAdvancedModal(true);
  };

  const handleOpenInSearchResults = () => {
    handleFilterClose();
    const state = (searchTerm || "").trim()
      ? chatListStateToExpandedSearchFilterState(searchTerm, filters, normalizedAdvanced)
      : chatListStateToSearchFilterState(searchTerm, filters, normalizedAdvanced);
    navigate(`/search-results?${filterStateToUrlString(state)}`);
  };

  const handleAdvancedApply = (payload) => {
    const state = modalPayloadToFilterState(payload);
    const mergedQuick = mergeQuickFiltersAfterAdvancedApply(filters, state.filters);

    if (typeof setFilters === "function") {
      setFilters(mergedQuick);
    }
    if (typeof applyChatFilterState === "function") {
      applyChatFilterState({ ...state, filters: mergedQuick, q: state.q ?? "" });
    }
    setShowAdvancedModal(false);
  };

  const getChannelTypeForConversation = (conv) => {
    const type = conv?.channelType ?? conv?.channel_type;
    if (type) return type;
    const channelId = conv?.channelId ?? conv?.id_origem ?? conv?.channel_id;
    if (channelId == null) return null;
    const ch = channels.find((c) => String(c.id) === String(channelId));
    return ch?.channelType ?? ch?.channel_type ?? null;
  };

  const getChannelTipoForConversation = (conv) => {
    const channelId = conv?.channelId ?? conv?.id_origem ?? conv?.channel_id;
    if (channelId == null) return null;
    const ch = channels.find((c) => String(c.id) === String(channelId));
    return ch?.tipo != null && ch?.tipo !== "" ? Number(ch.tipo) : null;
  };

  const isWhatsAppConversation = (conv) => {
    const type = getChannelTypeForConversation(conv);
    if (type && String(type).toLowerCase() === "whatsapp") return true;
    const channelName = (conv?.channel ?? conv?.channelLabel ?? "").toString().toLowerCase();
    return channelName.includes("whatsapp");
  };

  const isMetaMessagingConversation = (conv) => {
    const type = getChannelTypeForConversation(conv);
    const tipo = getChannelTipoForConversation(conv);
    return isMetaMessagingWindowChannel(type, tipo);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "—";
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : date.toLocaleDateString();
  };

  const sanitizeText = (htmlString) => {
    if (!htmlString) return "—";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    const text = (div.textContent || div.innerText || "").trim();
    return text || "—";
  };

  const formatListPreview = (conversation) => {
    const preview = conversation?.lastMessagePreview;
    const rawText = preview?.text || sanitizeText(conversation?.lastMessage);
    const text = sanitizeTelephonyListPreview(rawText) || "—";
    if (preview?.text) {
      const prefix =
        preview.kind === "client"
          ? "Cliente: "
          : preview.kind === "agent"
            ? "Equipe: "
            : "";
      return `${prefix}${text}`;
    }
    return text;
  };

  const getAgentId = (agent) => {
    if (!agent || typeof agent !== "object") return null;
    if (agent.id != null) return agent.id;
    if (agent.id_agente != null) return agent.id_agente;
    if (agent.agentId != null) return agent.agentId;

    const dynamicKey = Object.keys(agent).find((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes("id") &&
        agent[key] !== null &&
        agent[key] !== undefined &&
        (typeof agent[key] === "number" || !Number.isNaN(Number(agent[key])))
      );
    });

    return dynamicKey ? agent[dynamicKey] : null;
  };

  const buildAssigneeValue = (conversation) => {
    if (!conversation) {
      return { type: "queue", id: null, label: "Fila" };
    }

    const status = normalizeStatusInput(conversation?.status);
    const isClosed = status === 2 || status === 4 || status === 5;

    const assignedToValue =
      conversation?.assignedTo ?? conversation?.userName ?? null;

    const assignedIsQueueLabel =
      typeof assignedToValue === "string" &&
      ["fila", "queue"].includes(assignedToValue.toLowerCase());

    const hasUserByName =
      assignedToValue !== null &&
      assignedToValue !== undefined &&
      assignedToValue !== 0 &&
      assignedToValue !== "0" &&
      assignedToValue !== "" &&
      !assignedIsQueueLabel;

    const hasUserById =
      conversation?.userId !== null &&
      conversation?.userId !== undefined &&
      conversation?.userId !== 0 &&
      conversation?.userId !== "0";

    const agentName =
      conversation?.agentName ?? conversation?.agent_name ?? null;

    const rawAgentId =
      conversation?.agentId ??
      conversation?.id_agente ??
      conversation?.agenteId ??
      conversation?.idAgent ??
      null;

    const hasAgent = rawAgentId !== null && rawAgentId !== "" && rawAgentId !== 0 && rawAgentId !== "0";

    // Agente de IA tem prioridade sobre usuário
    if (hasAgent) {
      const id = rawAgentId;

      const fromList =
        agents.find((a) => String(getAgentId(a)) === String(id)) || null;

      const label =
        agentName ||
        fromList?.name ||
        fromList?.nome ||
        "Agente de IA";

      return { type: "agent", id, label };
    }

    if (hasUserByName || hasUserById) {
      const id = conversation?.userId ?? null;

      const fromList =
        id != null
          ? users.find((u) => String(u.id) === String(id)) || null
          : null;

      const isChatBot =
        fromList
          ? Number(fromList.ura) === 1
          : Number(conversation?.userUra) === 1;

      const baseLabel =
        assignedToValue ||
        fromList?.nome ||
        fromList?.name ||
        conversation?.userName ||
        "";

      const isCurrentUser =
        (id != null && String(id) === String(userData?.id)) ||
        (baseLabel && baseLabel === userData?.nome);

      const label = isCurrentUser
        ? "Minha Solicitação"
        : baseLabel || (isChatBot ? "ChatBot" : "Usuário");

      return { type: "user", id, label, isChatBot };
    }

    return {
      type: "queue",
      id: null,
      label: isClosed ? "Sem usuário" : "Fila",
    };
  };

  const [assigneeOverrides, setAssigneeOverrides] = React.useState({});
  const [statusUpdatingIds, setStatusUpdatingIds] = React.useState({});
  const [statusSnackbar, setStatusSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [automationBlockDialog, setAutomationBlockDialog] = React.useState({
    open: false,
    message: "",
  });

  const handleStatusChange = (conversation, newStatus) => {
    if (!conversation?.id) return;
    if (conversation.kind === "personal") return;
    if (statusUpdatingIds[conversation.id]) return;

    const conversationId = conversation.id;
    setStatusUpdatingIds((prev) => ({ ...prev, [conversationId]: true }));

    updateMutation.mutate(
      { id: conversationId, status: newStatus },
      {
        onSuccess: () => {
          setStatusUpdatingIds((prev) => {
            const next = { ...prev };
            delete next[conversationId];
            return next;
          });
        },
        onError: (err) => {
          setStatusUpdatingIds((prev) => {
            const next = { ...prev };
            delete next[conversationId];
            return next;
          });
          const fallback = tPane("pane.statusChangeError");
          if (isAutomationRuleBlockError(err)) {
            setAutomationBlockDialog({
              open: true,
              message: getAutomationBlockMessage(err, fallback),
            });
            return;
          }
          setStatusSnackbar({
            open: true,
            message: getRequestStatusChangeErrorMessage(err, fallback),
            severity: "error",
          });
        },
      }
    );
  };

  const handlePinUser = (conversation) => {
    if (!conversation?.id) return;
    if (conversation.kind === "personal") return;
    if (String(conversation.id).startsWith("wa-personal:")) return;
    updateMutation.mutate({ id: conversation.id, fixada_usuario: 1 });
  };

  /** Desafixa fixação pessoal e/ou global (canal/automação). */
  const handleUnpin = (conversation) => {
    if (!conversation?.id) return;
    if (conversation.kind === "personal") return;
    const payload = { id: conversation.id };
    if (conversation.fixadaUsuario) {
      payload.fixada_usuario = 0;
    }
    if (conversation.fixada) {
      payload.fixada = 0;
    }
    // pinned sem flags explícitas: limpa ambos
    if (
      conversation.pinned &&
      !Object.prototype.hasOwnProperty.call(payload, "fixada_usuario") &&
      !Object.prototype.hasOwnProperty.call(payload, "fixada")
    ) {
      payload.fixada_usuario = 0;
      payload.fixada = 0;
    }
    updateMutation.mutate(payload);
  };

  const handleAssigneeChange = (conversation, next) => {
    if (!conversation?.id || !next) return;
    if (conversation.kind === "personal") return;

    const payload = { id: conversation.id };

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;
    } else if (next.type === "agent") {
      // Resolve o ID do agente primeiro a partir do objeto bruto,
      // depois cai para o next.id e, por último, para o nome.
      let agentId = null;

      if (next.raw) {
        agentId = getAgentId(next.raw);
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agents.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId = getAgentId(found);
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;
    }

    setAssigneeOverrides((prev) => ({
      ...prev,
      [conversation.id]: next,
    }));

    updateMutation.mutate(payload);
  };

  const filtersToolbarPaperSx = {
    bgcolor: theme.palette.background.paper,
    borderBottom: 1,
    borderColor: "divider",
  };

  const searchField = (
    <TextField
      size="small"
      fullWidth
      placeholder={tChat("searchPlaceholder")}
      value={searchTerm}
      onChange={handleSearchChange}
      sx={{
        flex: 1,
        minWidth: 0,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        "& .MuiOutlinedInput-root": { borderRadius: 1 },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const meuDiaChip = (
    <Tooltip title={tChat("meuDiaTooltip")}>
      <Chip
        label={formatQuickFilterCountLabel(
          tChat("meuDia"),
          countsByLabel["Meu dia"]
        )}
        size="small"
        onClick={setMeuDia}
        variant={isMeuDiaActive ? "filled" : "outlined"}
        color={isMeuDiaActive ? "primary" : "default"}
        sx={{
          width: "100%",
          height: 34,
          borderRadius: 1,
          fontWeight: 600,
          justifyContent: "center",
          "& .MuiChip-label": {
            flex: 1,
            textAlign: "center",
            px: 1,
          },
        }}
      />
    </Tooltip>
  );

  const filtersMenuButton = (
    <Tooltip title={tChat("moreFiltersTooltip")}>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        aria-label={tChat("filtersMenuAria")}
        sx={{ flexShrink: 0 }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const filtersMenu = (
    <Menu
      anchorEl={filterAnchor}
      open={Boolean(filterAnchor)}
      onClose={handleFilterClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { minWidth: 280, maxHeight: 480 } }}
    >
      {savedFiltersMenu.length > 0 && [
        <ListSubheader key="sh-saved" sx={{ bgcolor: "transparent", lineHeight: 2 }}>
          {tChat("sectionSavedFilters")}
        </ListSubheader>,
        ...savedFiltersMenu.map((entry) => (
          <MenuItem key={entry.id} onClick={() => handleApplySavedFilter(entry)} dense>
            {entry.favorito ? (
              <StarIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
            ) : (
              <Box sx={{ width: 20, mr: 1 }} />
            )}
            <ListItemText
              primary={entry.nome}
              secondary={tChat("savedFilterApplyHint")}
              primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </MenuItem>
        )),
        <Divider key="div-saved" sx={{ my: 0.5 }} />,
      ]}
      <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
        {tChat("sectionWho")}
      </ListSubheader>
      {ownershipFilters.map((filter) => (
        <MenuItem key={filter} onClick={() => toggleFilter(filter)} dense>
          <Checkbox size="small" checked={filters.includes(filter)} sx={{ mr: 1 }} />
          <ListItemText primary={labeledFilter(filter)} />
        </MenuItem>
      ))}
      <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
        {tChat("sectionStatus")}
      </ListSubheader>
      {additionalFilters
        .filter((f) => f !== "Não Lidas")
        .map((filter) => (
          <MenuItem key={filter} onClick={() => toggleFilter(filter)} dense>
            <Checkbox size="small" checked={filters.includes(filter)} sx={{ mr: 1 }} />
            <ListItemText primary={labeledFilter(filter)} />
          </MenuItem>
        ))}
      <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
        {tChat("sectionOther")}
      </ListSubheader>
      <MenuItem onClick={() => toggleFilter("Pipeline de Vendas")} dense>
        <Checkbox size="small" checked={filters.includes("Pipeline de Vendas")} sx={{ mr: 1 }} />
        <ListItemText primary={labeledFilter("Pipeline de Vendas")} />
      </MenuItem>
      <MenuItem onClick={() => toggleFilter("Não Lidas")} dense>
        <Checkbox size="small" checked={filters.includes("Não Lidas")} sx={{ mr: 1 }} />
        <ListItemText primary={labeledFilter("Não Lidas")} />
      </MenuItem>
      {showPersonalWhatsappFilter && (
        <MenuItem onClick={() => toggleFilter(PERSONAL_WHATSAPP_QUICK_FILTER)} dense>
          <Checkbox
            size="small"
            checked={filters.includes(PERSONAL_WHATSAPP_QUICK_FILTER)}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={labeledFilter(PERSONAL_WHATSAPP_QUICK_FILTER)} />
        </MenuItem>
      )}
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={handleOpenAdvanced} dense sx={{ color: "primary.main" }}>
        <TuneRoundedIcon fontSize="small" sx={{ mr: 1.25 }} />
        <ListItemText
          primary={tChat("advancedSearchAction")}
          secondary={tChat("advancedSearchHint")}
          primaryTypographyProps={{ fontWeight: 600 }}
          secondaryTypographyProps={{ variant: "caption" }}
        />
      </MenuItem>
      <MenuItem onClick={handleOpenInSearchResults} dense sx={{ color: "primary.main" }}>
        <TableChartOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
        <ListItemText
          primary={tChat("openInSearchResultsAction")}
          secondary={tChat("openInSearchResultsHint")}
          primaryTypographyProps={{ fontWeight: 600 }}
          secondaryTypographyProps={{ variant: "caption" }}
        />
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      flexGrow={1}
      overflow="auto"
      className="sidebar-scroll"
      ref={listRef}
    >
      {/* Busca + atalho Meu dia + menu Filtros */}
      <Box
        className="tour-chat-list-filters"
        px={1.25}
        py={0.75}
        sx={filtersToolbarPaperSx}
      >
        <Stack spacing={0.75} width="100%">
          <Stack direction="row" spacing={0.5} alignItems="center">
            {searchField}
            {filtersMenuButton}
          </Stack>
          {meuDiaChip}
        </Stack>
        {filtersMenu}
        <AdvancedSearchModal
          open={showAdvancedModal}
          onClose={() => setShowAdvancedModal(false)}
          initialQuery={searchTerm}
          initialAdvanced={filterStateToModalInitial({
            ...normalizedAdvanced,
            filters,
          })}
          onApply={handleAdvancedApply}
          applyLabel={tChat("applyFiltersAction")}
        />
      </Box>

      {conversationsTimeoutMessage ? (
        <Alert severity="warning" sx={{ mx: 1.25, mt: 0.75 }}>
          {conversationsTimeoutMessage}
        </Alert>
      ) : null}

      {/* Pills dos filtros ativos — cores por status */}
      {(filters?.length > 0 || advancedFilterChips.length > 0) && (
        <FilterChipsCollapsible
          sx={{ px: 1.25, pb: 0.75, pt: 0.5 }}
          seeMoreLabel={tChat("showMoreFilters")}
          seeLessLabel={tChat("showLessFilters")}
        >
          {filters.map((filter) => {
            const statusStyle = getFilterPillStyle(filter, theme);
            return (
              <Chip
                key={filter}
                label={labeledFilter(filter)}
                size="small"
                onDelete={() => toggleFilter(filter)}
                sx={{
                  fontSize: "0.75rem",
                  height: 24,
                  ...statusStyle,
                }}
              />
            );
          })}
          {advancedFilterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              variant="outlined"
              onDelete={() => removeAdvancedChip(chip)}
              sx={{ fontSize: "0.75rem", height: 24 }}
            />
          ))}
          {filterStateHasAdvancedCriteria(normalizedAdvanced) && typeof clearAdvancedFilterState === "function" && (
            <Chip
              label={tChat("clearAdvancedFilters")}
              size="small"
              variant="outlined"
              onClick={clearAdvancedFilterState}
              sx={{ fontSize: "0.75rem", height: 24, borderStyle: "dashed" }}
            />
          )}
        </FilterChipsCollapsible>
      )}

      {/* Loading ao trocar filtros/busca (sempre visível abaixo dos pills) */}
      {isFetching && !initialLoading && (
        <Box
          sx={{
            px: 1.5,
            width: "100%",
            height: 0,
            overflow: "visible",
            position: "relative",
          }}
        >
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 4,
              borderRadius: 3,
            }}
          />
        </Box>
      )}

      {!initialLoading && !isFetching && filteredConversations.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 3,
            px: 2,
            textAlign: "center",
          }}
        >
          <SearchOffOutlinedIcon
            sx={{ fontSize: 32, color: "text.disabled", opacity: 0.35, mb: 1 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
            {emptyListVariant === "search" || emptyListVariant === "searchAndFilters"
              ? tChat("emptyWithSearch.title", { query: (searchTerm || "").trim() })
              : emptyListVariant === "filters"
                ? tChat("emptyWithFilters.title")
                : tChat("emptyDefault.title")}
          </Typography>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 0.5, maxWidth: 280, lineHeight: 1.5 }}
          >
            {emptyListVariant === "search"
              ? tChat("emptyWithSearch.subtitle")
              : emptyListVariant === "searchAndFilters"
                ? tChat("emptyWithSearchAndFilters.subtitle")
                : emptyListVariant === "filters"
                  ? tChat("emptyWithFilters.subtitle")
                  : tChat("emptyDefault.subtitle")}
          </Typography>

          {emptyListVariant !== "default" && (
            <Stack spacing={0.5} sx={{ mt: 1.5, width: "100%", maxWidth: 280 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenExpandedSearchResults}
                sx={{
                  textTransform: "none",
                  py: 0.5,
                  color: "text.secondary",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "action.hover", color: "primary.main" },
                }}
              >
                {tChat("emptyWithSearch.advancedSearchAction")}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearAllFilters}
                  sx={{
                    textTransform: "none",
                    py: 0.5,
                    color: "text.disabled",
                    fontWeight: 400,
                    "&:hover": { backgroundColor: "action.hover", color: "text.secondary" },
                  }}
                >
                  {tChat("emptyWithFilters.clearFiltersAction")}
                </Button>
              )}
            </Stack>
          )}

          {(emptyListVariant === "search" || emptyListVariant === "searchAndFilters") && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 1, maxWidth: 280, lineHeight: 1.45, opacity: 0.85 }}>
              {tChat("emptyWithSearch.advancedSearchHint")}
            </Typography>
          )}
        </Box>
      ) : (
        <>
      <List>
        {filteredConversations.map((conversation) => (
          <ListItem
            key={conversation.id}
            onClick={(event) => handleItemClick(event, conversation)}
            onContextMenu={(event) => handleContextMenu(event, conversation)}
            sx={{
              cursor: "pointer",
              backgroundColor:
                selectedConversation?.id === conversation.id
                  ? theme.palette.action.selected
                  : conversation.kind === "personal"
                    ? alpha("#128C7E", theme.palette.mode === "dark" ? 0.15 : 0.08)
                    : "transparent",
              "&:hover": { backgroundColor: theme.palette.action.hover },
              borderLeft: conversation.kind === "personal" ? "4px solid #128C7E" : "4px solid transparent",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "flex-start",
              padding: "12px 16px",
              gap: 1.5,
              py: 1.5,
              minHeight: 72,
            }}
          >
            <Badge
              color="error"
              variant="dot"
              overlap="circular"
              invisible={
                !conversation?.unreadClient ||
                Number(conversation?.unreadClient) === 0
              }
            >
              <Avatar
                src={getClientProfilePictureUrl(conversation?.profile_picture) || undefined}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: conversation.kind === "personal" ? "#128C7E" : theme.palette.primary.light,
                  color: conversation.kind === "personal" ? "#fff" : undefined,
                }}
              >
                {getInitials(conversation?.name)}
              </Avatar>
            </Badge>
            <Box flexGrow={1} minWidth={0} overflow="hidden" display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: "1 1 auto",
                    minWidth: 0,
                      fontWeight:
                        (conversation?.unreadClient ?? 0) > 0 ? 700 : 500,
                    fontSize: "0.9375rem",
                  }}
                >
                  {conversation.name || "—"}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} flexShrink={0}>
                  {conversation.pinned && conversation.kind !== "personal" && (
                    <Tooltip
                      title={
                        conversation.fixadaUsuario
                          ? tChat("pin.pinnedByYou")
                          : conversation.fixada
                            ? tChat("pin.pinnedByChannel")
                            : tChat("pin.pinned")
                      }
                      placement="left"
                      arrow
                    >
                      <PushPinIcon
                        sx={{
                          fontSize: 18,
                          color: theme.palette.text.secondary,
                          transform: "rotate(45deg)",
                        }}
                        aria-hidden
                      />
                    </Tooltip>
                  )}
                  {isWhatsAppConversation(conversation) &&
                    !isWhatsAppQrChannelTipo(getChannelTipoForConversation(conversation)) &&
                    (() => {
                      const lastClientAtRaw =
                        conversation.lastClientMessageAt ??
                        conversation.last_client_message_at ??
                        null;
                      const lastClientAt =
                        lastClientAtRaw != null && lastClientAtRaw !== ""
                          ? lastClientAtRaw
                          : conversation.timestamp;
                      const windowState = getWhatsAppWindowState(lastClientAt);
                      if (windowState.status === "aboutToExpire" || windowState.status === "expired") {
                        const iconColor =
                          windowState.status === "aboutToExpire"
                            ? theme.palette.warning.main
                            : theme.palette.error.main;
                        return (
                          <Tooltip title={windowState.tooltip} placement="left" arrow>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 24,
                                minHeight: 24,
                                color: iconColor,
                              }}
                              aria-label="Janela de 24h WhatsApp"
                            >
                              <ScheduleIcon sx={{ fontSize: 20 }} />
                            </Box>
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  {isMetaMessagingConversation(conversation) &&
                    (() => {
                      const channelType = getChannelTypeForConversation(conversation);
                      const lastClientAtRaw =
                        conversation.lastClientMessageAt ??
                        conversation.last_client_message_at ??
                        null;
                      const lastClientAt =
                        lastClientAtRaw != null && lastClientAtRaw !== ""
                          ? lastClientAtRaw
                          : conversation.timestamp;
                      const windowState = getMetaMessagingWindowState(lastClientAt, {
                        channelLabel: getMetaChannelLabel(channelType),
                      });
                      if (
                        windowState.status === "aboutToExpire" ||
                        windowState.status === "expired"
                      ) {
                        const iconColor =
                          windowState.status === "aboutToExpire"
                            ? theme.palette.warning.main
                            : theme.palette.error.main;
                        return (
                          <Tooltip title={windowState.tooltip} placement="left" arrow>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 24,
                                minHeight: 24,
                                color: iconColor,
                              }}
                              aria-label="Janela de 24h Instagram/Messenger"
                            >
                              <ScheduleIcon sx={{ fontSize: 20 }} />
                            </Box>
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  <Typography variant="caption" color={theme.palette.text.secondary} sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                    {formatTimestamp(conversation.timestamp)}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color={theme.palette.text.secondary}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "0.8125rem",
                  lineHeight: 1.35,
                }}
              >
                {formatListPreview(conversation)}
              </Typography>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mt={0.5}>
                {conversation.kind !== "personal" &&
                  getChannelIcon(
                    conversation.channel ?? null,
                    getChannelTypeForConversation(conversation),
                    getChannelTipoForConversation(conversation)
                  )}
                {conversation.kind !== "personal" && (
                  <Tooltip title={`Protocolo: ${conversation?.id || ""}`}>
                    <Chip
                      label={`#${conversation?.id || ""}`}
                      size="small"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: "22px",
                        maxWidth: "100px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </Tooltip>
                )}
                {conversation.kind !== "personal" &&
                  Number(conversation.relationshipsCount) > 0 && (
                    <ClientRelationshipBadge
                      count={conversation.relationshipsCount}
                      hint={conversation.relationshipHint}
                      compact
                    />
                  )}

                {conversation.kind !== "personal" ? (
                  <>
                    <AssigneeSelector
                      value={assigneeOverrides[conversation.id] ?? buildAssigneeValue(conversation)}
                      onChange={(next) => handleAssigneeChange(conversation, next)}
                      users={users}
                      agents={agents}
                      loading={usersLoading}
                      size="small"
                    />

                    <StatusChipSelector
                      currentStatus={conversation.status}
                      onStatusChange={(newStatus) =>
                        handleStatusChange(conversation, newStatus)
                      }
                      size="small"
                      loading={Boolean(statusUpdatingIds[conversation.id])}
                    />
                  </>
                ) : (
                  <Chip
                    label={tChat("filters.personalWhatsapp")}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 22 }}
                  />
                )}
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
      <div ref={bottomRef} style={{ height: 1 }} />

      {isFetchingNextPage && (
        <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
        </>
      )}

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuAnchor ? { top: contextMenuAnchor.mouseY, left: contextMenuAnchor.mouseX } : undefined
        }
        open={Boolean(contextMenuAnchor)}
        onClose={handleContextMenuClose}
      >
        {selectedForContextMenu &&
          selectedForContextMenu.kind !== "personal" &&
          !String(selectedForContextMenu.id || "").startsWith("wa-personal:") &&
          (selectedForContextMenu.pinned ? (
            <MenuItem
              onClick={() => {
                handleUnpin(selectedForContextMenu);
                handleContextMenuClose();
              }}
            >
              {tChat("pin.unpin")}
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                handlePinUser(selectedForContextMenu);
                handleContextMenuClose();
              }}
            >
              {tChat("pin.pinToTop")}
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => {
            handleOpenInPopup(selectedForContextMenu);
            handleContextMenuClose();
          }}
        >
          Abrir em nova janela
        </MenuItem>
      </Menu>

      <AutomationActionBlockedDialog
        open={automationBlockDialog.open}
        message={automationBlockDialog.message}
        onClose={() => setAutomationBlockDialog({ open: false, message: "" })}
      />

      <Snackbar
        open={statusSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setStatusSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={statusSnackbar.severity}
          variant="filled"
          onClose={() => setStatusSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%", maxWidth: 560 }}
        >
          {statusSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConversationList;
